if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.colorData = this.querySelector('[data-sjbjj-gallery-map]');
        const automatic = this.querySelector('[data-sjbjj-auto-media]');
        this.colorMap = this.colorData ? JSON.parse(this.colorData.textContent) :
          automatic && window.sjbjjResolveMedia?.(JSON.parse(automatic.textContent));
        if (this.colorMap) this.initializeColorGallery();
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 180));
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initializeColorGallery() {
        this.colorGroups = new Map();
        const slides = this.elements.viewer.querySelector('ul');
        const thumbs = this.elements.thumbnails?.querySelector('ul');
        const activeId = slides.querySelector('.is-active')?.dataset.mediaId;
        const section = this.id.replace('MediaGallery-', '');
        this.colorMap.groups.forEach(group => {
          group.ids.forEach((id, index) => {
            const fullId = section + '-' + id;
            this.colorGroups.set(fullId, group);
            const slide = slides.querySelector('[data-media-id="' + fullId + '"]');
            if (slide) {
              slides.append(slide);
              const image = slide.querySelector('.product__modal-opener--image img');
              if (image) image.alt = [group.color, index === 0 ? this.dataset.frontLabel : index === 1 ? this.dataset.backLabel : ''].filter(Boolean).join(' — ');
            }
            const thumbnail = thumbs?.querySelector('[data-target="' + fullId + '"]');
            if (thumbnail) {
              thumbs.append(thumbnail);
              thumbnail.querySelector('button').setAttribute('aria-label', [group.color, index === 0 ? this.dataset.frontLabel : index === 1 ? this.dataset.backLabel : ''].filter(Boolean).join(' — '));
            }
          });
        });
        Array.from(slides.children).filter(slide => !this.colorGroups.has(slide.dataset.mediaId)).forEach(slide => slides.append(slide));
        if (thumbs) Array.from(thumbs.children).filter(thumb => !this.colorGroups.has(thumb.dataset.target)).forEach(thumb => thumbs.append(thumb));
        thumbs?.querySelectorAll('[data-target]').forEach((thumb, index) => thumb.dataset.mediaPosition = index + 1);
        this.dataset.colorSync = 'true';
        this.closest('product-info')?.addEventListener('click', event => {
          const label = event.target.closest('label');
          const input = label ? document.getElementById(label.htmlFor) : event.target;
          if (input?.tagName !== 'INPUT' || !input.name.trim().startsWith(this.colorMap.option + '-')) return;
          const group = this.colorMap.groups.find(group => group.color === input.value);
          if (!group) return;
          this.gallerySelection = null;
          this.setActiveMedia(section + '-' + group.front, false, false);
        });
        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
        const selectedData = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        const variant = selectedData ? JSON.parse(selectedData.textContent) : null;
        if (activeId) this.activateVariantMedia(activeId.split('-').pop(), variant);
      }

      activateVariantMedia(mediaId, variant) {
        const prefix = this.id.replace('MediaGallery-', '') + '-';
        const fullId = prefix + mediaId;
        const selectedColor = variant?.options?.[this.colorMap.position - 1];
        const group = selectedColor ? this.colorMap.groups.find(group => group.color === selectedColor) : this.colorGroups?.get(fullId);
        this.toggleAttribute('data-color-photos-missing', Boolean(selectedColor && !group));
        const missingStatus = this.querySelector('[data-gallery-missing]');
        if (missingStatus) missingStatus.hidden = !(selectedColor && !group);
        if (selectedColor && !group) return;
        const requestedGroup = this.colorGroups?.get(this.gallerySelection);
        const selectedId = group && requestedGroup === group ? this.gallerySelection : group ? prefix + group.front : fullId;
        this.setActiveMedia(selectedId, false, false);
      }

      syncColor(mediaId) {
        const group = this.colorGroups?.get(mediaId);
        if (!group) return;
        this.gallerySelection = mediaId;
        const productInfo = this.closest('product-info');
        const picker = productInfo?.querySelector('variant-selects');
        if (!picker) return;
        const inputs = Array.from(picker.querySelectorAll('input[type="radio"]'));
        const colorInput = inputs.find(input => input.value === group.color && input.name.trim().startsWith(this.colorMap.option + '-'));
        if (colorInput) {
          if (colorInput.checked) return;
          colorInput.checked = true;
          const event = new Event('change', { bubbles: true });
          event.sjbjjGallery = true;
          colorInput.dispatchEvent(event);
          return;
        }
        const select = Array.from(picker.querySelectorAll('select')).find(select => Array.from(select.options).some(option => option.value === group.color));
        if (select && select.value !== group.color) {
          select.value = group.color;
          const event = new Event('change', { bubbles: true });
          event.sjbjjGallery = true;
          select.dispatchEvent(event);
        }
      }

      onSlideChanged(event) {
        const current = this.elements.viewer.sliderItemsToShow?.[this.elements.viewer.currentPage - 1] || event.detail.currentElement;
        if (!current) return;
        const mediaId = current.dataset.mediaId;
        const thumbnail = this.elements.thumbnails?.querySelector('[data-target="' + mediaId + '"]');
        this.setActiveThumbnail(thumbnail);
        if (this.colorMap) {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach(slide => slide.classList.toggle('is-active', slide === current));
          this.syncColor(mediaId);
        }
      }

      setActiveMedia(mediaId, prepend, synchronize = true) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend && !this.colorMap) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: this.colorMap ? 'instant' : 'auto' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        if (synchronize && this.colorMap) this.syncColor(activeMedia.dataset.mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}


